import {
  BankDetail,
  BankDetailStatusEnum,
  Client,
  PaymentRule,
  Title,
  WorkspaceTypeEnum,
} from '@sr-sdks/api-sdk-axios';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DropdownMenuItem,
  Icon,
  SR_ICONS,
  Separator,
} from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { queryOptionsGetTitlesPaginated } from '../api/titles/titlesApi';
import { queryOptionsGetClientsPaginated } from '../api/workspaces/clientApi';
import { FEATURE_FLAGS } from '../constant/featureFlags';
import { bankDetailsService } from '../reducers/payments/bankDetailSlice';
import { paymentRuleService } from '../reducers/payments/paymentRuleSlice';
import { workspaceService } from '../reducers/workspaces/workspaceSlice';
import { useAppDispatch, useTypedSelector } from '../stateStore';
import {
  SRBadgeProps,
  isLoadingState,
  pluralize,
  workspaceTypeIsAgent,
} from '../utils/helpers/common';
import { SuggestionProps } from './AppAutocomplete';
import { AppBadge } from './Badges/AppBadge';
import { StatusBadge } from './Badges/StatusBadge';
import { ATTRIBUTE_BADGE_PRESETS } from './Badges/appBadgeConstants';
import { BankAccountPaymentRuleFormV3 } from './BankAccountPaymentRuleFormV3';
import { IfFeatureEnabledMSR3 } from './IfFeatureEnabledMSR3';
import AppLoader from './Loader';
import OptionsDropdown from './OptionsDropdown';
import { DefaultPaymentRuleProps } from './PaymentRuleForm';
import LookupClientName from './lookups/LookupClientName';
import LookupTitleName from './lookups/LookupTitleName';

// Stable references so a still-loading (or absent) query doesn't feed the
// `defaultPaymentRule` memo below a fresh `[]` on every render (see
// ReviewClients.tsx's `NO_CLIENTS`).
const NO_CLIENTS: Client[] = [];
const NO_TITLES: Title[] = [];

type PendingRuleAction = () => void;

export type BankAccountCardV3Props = {
  bankDetail: BankDetail;
  isStaffView?: boolean;
  onDeleteAccountClick?: (bankAccount: BankDetail) => void;
  onEditAccountClick?: (id: string) => void;
  onViewAccountClick?: (id: string) => void;
};

type BankAccountCardV3HeaderProps = {
  bankDetail: BankDetail;
  hasPaymentRules: boolean;
  isStaffView?: boolean;
  onAddPaymentRuleClick: () => void;
  onDeleteAccountClick?: (bankAccount: BankDetail) => void;
  onEditAccountClick?: (id: string) => void;
  onSetDefaultClick: () => void;
  onViewAccountClick?: (id: string) => void;
};

type BankAccountCardV3RulesSectionProps = {
  bankDetail: BankDetail;
  defaultPaymentRule?: DefaultPaymentRuleProps;
  editableRuleIndex?: number;
  isAddingRule: boolean;
  isExpanded: boolean;
  isStaffView?: boolean;
  onAddRuleClick: () => void;
  onCancelAddClick: () => void;
  onCancelEditClick: () => void;
  onDeleteRuleClick: (rule: PaymentRule) => void;
  onDirtyChange: (isDirty: boolean) => void;
  onEditRuleClick: (rule: PaymentRule, index: number) => void;
  onEditSuccess: () => void;
  onExpandedChange: (isExpanded: boolean) => void;
  paymentRule?: PaymentRule;
  paymentRules: PaymentRule[];
  workspaceType?: WorkspaceTypeEnum;
};

type BankAccountCardV3RuleItemProps = {
  index: number;
  isStaffView?: boolean;
  onDeleteClick: (rule: PaymentRule) => void;
  onEditClick: (rule: PaymentRule, index: number) => void;
  rule: PaymentRule;
};

const BankAccountCardV3Header = ({
  bankDetail,
  hasPaymentRules,
  isStaffView,
  onAddPaymentRuleClick,
  onDeleteAccountClick,
  onEditAccountClick,
  onSetDefaultClick,
  onViewAccountClick,
}: BankAccountCardV3HeaderProps) => {
  const canAddPaymentRule =
    bankDetail.bankDetailStatus === BankDetailStatusEnum.Confirmed &&
    !bankDetail.isDefault &&
    hasPaymentRules;

  return (
    <div className="flex items-start gap-4">
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="break-words text-base font-semibold text-neutral-950">
            {bankDetail.bankName || bankDetail.accountName}
          </h3>

          {bankDetail.isDefault && (
            <AppBadge type={ATTRIBUTE_BADGE_PRESETS.defaultAccount} />
          )}

          {bankDetail.bankDetailStatus && (
            <StatusBadge type={bankDetail.bankDetailStatus} />
          )}
        </div>

        <p className="text-sm font-medium text-neutral-700">
          {bankDetail.accountNumberMasked}
        </p>
      </div>

      <div className="shrink-0">
        {isStaffView ? (
          <OptionsDropdown align="end">
            <DropdownMenuItem
              onClick={() => onViewAccountClick?.(bankDetail._id)}
            >
              View Details
            </DropdownMenuItem>
          </OptionsDropdown>
        ) : (
          <OptionsDropdown align="end">
            {!bankDetail.isDefault &&
              bankDetail.bankDetailStatus ===
                BankDetailStatusEnum.Confirmed && (
                <DropdownMenuItem onClick={onSetDefaultClick}>
                  Set as Default
                </DropdownMenuItem>
              )}

            <DropdownMenuItem
              onClick={() => onEditAccountClick?.(bankDetail._id)}
            >
              Edit Bank Account
            </DropdownMenuItem>

            {canAddPaymentRule && (
              <IfFeatureEnabledMSR3 name={FEATURE_FLAGS.MSR3_ADD_PAYMENT_RULE}>
                <DropdownMenuItem onClick={onAddPaymentRuleClick}>
                  Add Payment Rule
                </DropdownMenuItem>
              </IfFeatureEnabledMSR3>
            )}

            {!bankDetail.isDefault && (
              <IfFeatureEnabledMSR3
                name={FEATURE_FLAGS.MSR3_DELETE_BANK_ACCOUNT}
              >
                <>
                  <Separator className="h-px" />
                  <DropdownMenuItem
                    onClick={() => onDeleteAccountClick?.(bankDetail)}
                  >
                    Delete Bank Account
                  </DropdownMenuItem>
                </>
              </IfFeatureEnabledMSR3>
            )}
          </OptionsDropdown>
        )}
      </div>
    </div>
  );
};

const BankAccountCardV3RuleItem = ({
  index,
  isStaffView,
  onDeleteClick,
  onEditClick,
  rule,
}: BankAccountCardV3RuleItemProps) => {
  const serviceLabel = SRBadgeProps[rule.serviceType]?.label.toUpperCase();

  return (
    <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-start">
      <p className="text-sm font-bold text-neutral-950">Rule {index + 1}</p>

      <div className="min-w-0 flex-1 space-y-2 text-sm text-neutral-700">
        <p>
          When Client is <LookupClientName id={rule.clientId} />
        </p>
        <p>
          And Title is <LookupTitleName id={rule.titleId} />
        </p>
        <p>
          And Service is <strong>{serviceLabel}</strong>
        </p>
      </div>

      {!isStaffView && (
        <div className="flex gap-2 sm:justify-end">
          <Button
            className="h-8 w-8"
            icon={<Icon icon={SR_ICONS.edit} weight="fill" />}
            size="icon"
            variant="outline"
            onClick={() => onEditClick(rule, index)}
          >
            <span className="sr-only">Edit Payment Rule</span>
          </Button>
          <Button
            className="h-8 w-8"
            icon={<Icon icon="delete" weight="fill" />}
            size="icon"
            variant="outline"
            onClick={() => onDeleteClick(rule)}
          >
            <span className="sr-only">Delete Payment Rule</span>
          </Button>
        </div>
      )}
    </div>
  );
};

const BankAccountCardV3RulesSection = ({
  bankDetail,
  defaultPaymentRule,
  editableRuleIndex,
  isAddingRule,
  isExpanded,
  isStaffView,
  onAddRuleClick,
  onCancelAddClick,
  onCancelEditClick,
  onDeleteRuleClick,
  onDirtyChange,
  onEditRuleClick,
  onEditSuccess,
  onExpandedChange,
  paymentRule,
  paymentRules,
  workspaceType,
}: BankAccountCardV3RulesSectionProps) => (
  <Collapsible open={isExpanded} onOpenChange={onExpandedChange}>
    <div className="flex flex-col gap-3 border-t border-neutral-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-neutral-600">
        This account receives payments based on{' '}
        {pluralize(paymentRules.length, 'rule', 'rules')}
      </p>

      <CollapsibleTrigger asChild>
        <Button className="w-fit px-0 text-sr-red" variant="transparent">
          {isExpanded ? 'Hide Payment Rules' : 'Show Payment Rules'}
          <Icon icon={isExpanded ? 'chevronDown' : 'chevronRight'} />
        </Button>
      </CollapsibleTrigger>
    </div>

    <CollapsibleContent className="pt-3">
      <div className="divide-y divide-neutral-200">
        {paymentRules.map((rule, index) =>
          index === editableRuleIndex ? (
            <BankAccountPaymentRuleFormV3
              key={rule._id}
              bankDetail={bankDetail}
              className="py-4"
              defaultPaymentRule={defaultPaymentRule}
              paymentRuleCount={index + 1}
              paymentRuleId={paymentRule?._id}
              workspaceType={workspaceType}
              onCancelClick={onCancelEditClick}
              onDirtyChange={onDirtyChange}
              onSuccess={onEditSuccess}
            />
          ) : (
            <BankAccountCardV3RuleItem
              key={rule._id}
              index={index}
              isStaffView={isStaffView}
              rule={rule}
              onDeleteClick={onDeleteRuleClick}
              onEditClick={onEditRuleClick}
            />
          ),
        )}

        {isAddingRule && (
          <BankAccountPaymentRuleFormV3
            bankDetail={bankDetail}
            className="py-4"
            paymentRuleCount={paymentRules.length + 1}
            workspaceType={workspaceType}
            onCancelClick={onCancelAddClick}
            onDirtyChange={onDirtyChange}
            onSuccess={onEditSuccess}
          />
        )}
      </div>

      {!isStaffView && !isAddingRule && (
        <div className="flex justify-start pt-4 sm:justify-end">
          <Button
            icon={<Icon icon="plus" weight="bold" />}
            size="sm"
            variant="outline"
            onClick={onAddRuleClick}
          >
            Add Rule
          </Button>
        </div>
      )}
    </CollapsibleContent>
  </Collapsible>
);

export const BankAccountCardV3 = ({
  bankDetail,
  isStaffView,
  onDeleteAccountClick,
  onEditAccountClick,
  onViewAccountClick,
}: BankAccountCardV3Props) => {
  const dispatch = useAppDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowPaymentRule, setShouldShowPaymentRule] = useState(false);
  const [isCardLoading, setIsCardLoading] = useState(true);
  const [paymentRules, setPaymentRules] = useState<PaymentRule[]>([]);
  const [paymentRule, setPaymentRule] = useState<PaymentRule>();
  const [editableRuleIndex, setEditableRuleIndex] = useState<number>();
  const [toDeletePaymentRule, setToDeletePaymentRule] = useState<PaymentRule>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDefaultModal, setShowDefaultModal] = useState(false);
  const [isRuleFormDirty, setIsRuleFormDirty] = useState(false);
  const [pendingRuleAction, setPendingRuleAction] =
    useState<PendingRuleAction>();
  const [showDiscardRuleChangesModal, setShowDiscardRuleChangesModal] =
    useState(false);

  const workspace = useTypedSelector(workspaceService.selectors.workspace);
  const isRemoving = useTypedSelector(paymentRuleService.selectors.isRemoving);
  const isSetDefaulting = useTypedSelector(
    bankDetailsService.selectors.isSetDefaulting,
  );

  const { data: clientsData } = useQuery(
    queryOptionsGetClientsPaginated({
      page: 1,
      pageSize: 100,
      sortBy: 'created:asc',
      workspaceId: bankDetail.workspaceId,
    }),
  );
  const clients = clientsData?.items ?? NO_CLIENTS;

  const { data: titlesData } = useQuery(
    queryOptionsGetTitlesPaginated({
      page: 1,
      pageSize: 100,
      sortBy: 'created:asc',
      workspaceId: bankDetail.workspaceId,
    }),
  );
  const titles = titlesData?.items ?? NO_TITLES;

  const validBankDetail =
    !bankDetail.isDefault &&
    !isCardLoading &&
    bankDetail.bankDetailStatus === BankDetailStatusEnum.Confirmed;
  const paymentRulesExist = paymentRules.length > 0;

  const defaultPaymentRule = useMemo<
    DefaultPaymentRuleProps | undefined
  >(() => {
    if (!workspace) return undefined;
    if (!paymentRule || titles.length === 0) return undefined;
    if (workspaceTypeIsAgent(workspace) && clients.length === 0)
      return undefined;

    const clientOption: SuggestionProps =
      paymentRule.clientId === '*'
        ? { key: '*', value: 'ALL CLIENTS' }
        : (() => {
            const matchedClient = clients.find(
              (client) => client._id === paymentRule.clientId,
            );

            return {
              key: matchedClient?._id ?? paymentRule.clientId,
              value:
                matchedClient?.clientName.toUpperCase() ?? 'UNKNOWN CLIENT',
            };
          })();

    const titleOption: SuggestionProps =
      paymentRule.titleId === '*'
        ? { key: '*', value: 'ALL TITLES' }
        : (() => {
            const matchedTitle = titles.find(
              (title) => title._id === paymentRule.titleId,
            );

            return {
              key: matchedTitle?._id ?? paymentRule.titleId,
              value: matchedTitle?.title.toUpperCase() ?? 'UNKNOWN TITLE',
            };
          })();

    const serviceTypeOption: SuggestionProps = {
      key: paymentRule.serviceType,
      value:
        SRBadgeProps[paymentRule.serviceType]?.label.toUpperCase() ??
        paymentRule.serviceType.toUpperCase(),
    };

    return {
      client: clientOption,
      serviceType: serviceTypeOption,
      title: titleOption,
    };
  }, [clients, paymentRule, titles, workspace]);

  const refreshPaymentRules = useCallback(async () => {
    try {
      const paymentRuleResponse = await dispatch(
        paymentRuleService.findAll({
          bankDetailsId: bankDetail._id,
          workspaceId: bankDetail.workspaceId,
        }),
      );

      if (paymentRuleService.findAll.fulfilled.match(paymentRuleResponse)) {
        setPaymentRules(paymentRuleResponse.payload as PaymentRule[]);
      }
    } finally {
      setIsCardLoading(false);
    }
  }, [bankDetail._id, bankDetail.workspaceId, dispatch]);

  useEffect(() => {
    refreshPaymentRules();
  }, [refreshPaymentRules]);

  const requestRuleFormAction = (action: PendingRuleAction) => {
    if (!isRuleFormDirty) {
      action();

      return;
    }

    setPendingRuleAction(() => action);
    setShowDiscardRuleChangesModal(true);
  };

  const handleConfirmDiscardRuleChanges = () => {
    const action = pendingRuleAction;

    setPendingRuleAction(undefined);
    setShowDiscardRuleChangesModal(false);
    setIsRuleFormDirty(false);
    action?.();
  };

  const handleCancelDiscardRuleChanges = () => {
    setPendingRuleAction(undefined);
    setShowDiscardRuleChangesModal(false);
  };

  const handleAddPaymentRuleClick = () => {
    requestRuleFormAction(() => {
      setShouldShowPaymentRule(true);
      setIsExpanded(true);
      setEditableRuleIndex(undefined);
      setPaymentRule(undefined);
      setIsRuleFormDirty(false);
    });
  };

  const handleDeletePaymentRule = async () => {
    if (!toDeletePaymentRule) return;

    const removePaymentRuleResponse = await dispatch(
      paymentRuleService.remove({ paymentRuleId: toDeletePaymentRule._id }),
    );

    if (paymentRuleService.remove.fulfilled.match(removePaymentRuleResponse)) {
      await refreshPaymentRules();
      setShowDeleteModal(false);
      setIsExpanded(true);
      setToDeletePaymentRule(undefined);
    }
  };

  const handleSetDefault = async () => {
    const setDefaultResponse = await dispatch(
      bankDetailsService.setDefault({
        id: bankDetail._id,
      }),
    );

    if (bankDetailsService.setDefault.fulfilled.match(setDefaultResponse)) {
      await dispatch(
        bankDetailsService.findAll({
          sortBy: 'isDefault:desc',
          workspaceId: bankDetail.workspaceId,
        }),
      );
    }

    setShowDefaultModal(false);
  };

  const handleRuleFormSuccess = async () => {
    setShouldShowPaymentRule(false);
    setEditableRuleIndex(undefined);
    setPaymentRule(undefined);
    setIsRuleFormDirty(false);
    await refreshPaymentRules();
    setIsExpanded(true);
  };

  return (
    <Card className="max-h-max rounded-lg border border-neutral-200 shadow-none">
      <CardContent className="space-y-4 p-4 sm:p-5">
        <BankAccountCardV3Header
          bankDetail={bankDetail}
          hasPaymentRules={paymentRulesExist}
          isStaffView={isStaffView}
          onAddPaymentRuleClick={handleAddPaymentRuleClick}
          onDeleteAccountClick={onDeleteAccountClick}
          onEditAccountClick={onEditAccountClick}
          onSetDefaultClick={() => setShowDefaultModal(true)}
          onViewAccountClick={onViewAccountClick}
        />

        <IfFeatureEnabledMSR3 name={FEATURE_FLAGS.MSR3_ADD_PAYMENT_RULE}>
          {(isCardLoading || isLoadingState(isRemoving)) && (
            <AppLoader.FlexLoader className="h-16" />
          )}

          {validBankDetail && !paymentRulesExist && !shouldShowPaymentRule && (
            <Alert
              actionButtonProps={{
                children: 'Add Rule',
                onClick: handleAddPaymentRuleClick,
              }}
              hasActionButton={!isStaffView}
              variant="warning"
            >
              {isStaffView
                ? 'In order for this account to receive a payment, the member must set up a payment rule.'
                : 'In order for this account to receive a payment, please set up a payment rule'}
            </Alert>
          )}

          {validBankDetail && paymentRulesExist && (
            <BankAccountCardV3RulesSection
              bankDetail={bankDetail}
              defaultPaymentRule={defaultPaymentRule}
              editableRuleIndex={editableRuleIndex}
              isAddingRule={shouldShowPaymentRule}
              isExpanded={isExpanded}
              isStaffView={isStaffView}
              paymentRule={paymentRule}
              paymentRules={paymentRules}
              workspaceType={workspace?.workspaceType}
              onAddRuleClick={handleAddPaymentRuleClick}
              onCancelAddClick={() => {
                setShouldShowPaymentRule(false);
                setPaymentRule(undefined);
                setIsRuleFormDirty(false);
              }}
              onCancelEditClick={() => {
                setEditableRuleIndex(undefined);
                setPaymentRule(undefined);
                setIsRuleFormDirty(false);
              }}
              onDeleteRuleClick={(rule) => {
                setToDeletePaymentRule(rule);
                setShowDeleteModal(true);
              }}
              onDirtyChange={setIsRuleFormDirty}
              onEditRuleClick={(rule, index) => {
                requestRuleFormAction(() => {
                  setShouldShowPaymentRule(false);
                  setEditableRuleIndex(index);
                  setPaymentRule(rule);
                  setIsRuleFormDirty(false);
                });
              }}
              onEditSuccess={handleRuleFormSuccess}
              onExpandedChange={(nextIsExpanded) => {
                if (nextIsExpanded) {
                  setIsExpanded(true);

                  return;
                }

                requestRuleFormAction(() => {
                  setIsExpanded(false);
                  setShouldShowPaymentRule(false);
                  setEditableRuleIndex(undefined);
                  setPaymentRule(undefined);
                  setIsRuleFormDirty(false);
                });
              }}
            />
          )}

          {validBankDetail && !paymentRulesExist && shouldShowPaymentRule && (
            <BankAccountPaymentRuleFormV3
              bankDetail={bankDetail}
              paymentRuleCount={paymentRules.length + 1}
              workspaceType={workspace?.workspaceType}
              onCancelClick={() => {
                setShouldShowPaymentRule(false);
                setPaymentRule(undefined);
                setIsRuleFormDirty(false);
              }}
              onDirtyChange={setIsRuleFormDirty}
              onSuccess={handleRuleFormSuccess}
            />
          )}
        </IfFeatureEnabledMSR3>
      </CardContent>

      {showDeleteModal && (
        <Dialog
          heading="Are you sure you want to delete this payment rule?"
          actionButtonProps={{
            children: 'Delete',
            loading: isLoadingState(isRemoving),
            onClick: handleDeletePaymentRule,
          }}
          cancelButtonProps={{
            children: 'Cancel',
            onClick: () => {
              setShowDeleteModal(false);
              setToDeletePaymentRule(undefined);
            },
          }}
          hasCancelButton
          open={showDeleteModal}
          onOpenChange={() => setShowDeleteModal(false)}
        >
          <div className="font-sans text-base leading-normal">
            {paymentRules.length === 1
              ? 'The bank account will not receive any payments until a rule is created.'
              : 'This payment rule will be permanently deleted.'}
          </div>
        </Dialog>
      )}

      {showDefaultModal && (
        <Dialog
          heading="Are you sure you want to set this bank account as the new default?"
          actionButtonProps={{
            children: 'Set as Default',
            loading: isLoadingState(isSetDefaulting),
            onClick: handleSetDefault,
          }}
          cancelButtonProps={{
            onClick: () => setShowDefaultModal(false),
          }}
          open={showDefaultModal}
          onOpenChange={() => setShowDefaultModal(false)}
        >
          <div className="font-sans text-base leading-normal">
            This will remove any existing payment rules associated with the bank
            account you have chosen.
          </div>
        </Dialog>
      )}

      {showDiscardRuleChangesModal && (
        <Dialog
          heading="Discard unsaved payment rule changes?"
          actionButtonProps={{
            children: 'Discard changes',
            onClick: handleConfirmDiscardRuleChanges,
          }}
          cancelButtonProps={{
            children: 'Keep editing',
            onClick: handleCancelDiscardRuleChanges,
          }}
          hasCancelButton
          open={showDiscardRuleChangesModal}
          onOpenChange={(open) => {
            if (!open) handleCancelDiscardRuleChanges();
          }}
        >
          <div className="font-sans text-base leading-normal">
            You have unsaved payment rule changes. If you continue, those
            changes will be lost.
          </div>
        </Dialog>
      )}
    </Card>
  );
};
